import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Plus, BookOpen, Loader2, Search, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAdmin } from '../contexts/AdminContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Header } from '../components/Header';
import { SurahCard } from '../components/SurahCard';
import { SurahModal } from '../components/SurahModal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { AudioPlayer } from '../components/AudioPlayer';
import { getSurahs, createSurah, updateSurah, deleteSurah } from '../lib/api';
import { toast } from 'sonner';

export default function SurahListPage({ onSelectSurah, onOpenMutashabihat }) {
  const { t } = useLanguage();
  const { isAdmin } = useAdmin();
  
  const [surahs, setSurahs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showSurahModal, setShowSurahModal] = useState(false);
  const [editingSurah, setEditingSurah] = useState(null);
  const [deletingSurah, setDeletingSurah] = useState(null);

  // Filter surahs based on search query
  const filteredSurahs = useMemo(() => {
    if (!searchQuery.trim()) return surahs;
    
    const query = searchQuery.toLowerCase().trim();
    return surahs.filter(surah => 
      surah.name_arabic.includes(query) ||
      surah.name_french.toLowerCase().includes(query) ||
      surah.number.toString().includes(query)
    );
  }, [surahs, searchQuery]);

  // Fetch surahs with offline support
  const fetchSurahs = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getSurahs();
      const data = response.data || [];
      setSurahs(data.sort((a, b) => a.number - b.number));
      
      // Cache data in localStorage for offline fallback
      if (data.length > 0) {
        localStorage.setItem('quran-cached-surahs', JSON.stringify(data));
      }
    } catch (error) {
      console.error('Failed to fetch surahs:', error);
      
      // Try to load from localStorage cache
      const cachedData = localStorage.getItem('quran-cached-surahs');
      if (cachedData) {
        try {
          const data = JSON.parse(cachedData);
          setSurahs(data.sort((a, b) => a.number - b.number));
          toast.info(t('offlineCache'));
        } catch (e) {
          toast.error(t('cannotLoadExcerpts'));
        }
      } else if (!navigator.onLine) {
        toast.error(t('offlineNoData'));
      } else {
        toast.error(t('error'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSurahs();
  }, [fetchSurahs]);

  // Handle save surah
  const handleSaveSurah = async (data) => {
    try {
      setIsSaving(true);
      if (editingSurah) {
        await updateSurah(editingSurah.id, data);
        toast.success(t('success'));
      } else {
        await createSurah(data);
        toast.success(t('success'));
      }
      await fetchSurahs();
      setShowSurahModal(false);
      setEditingSurah(null);
    } catch (error) {
      console.error('Failed to save surah:', error);
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete surah
  const handleDeleteSurah = async () => {
    if (!deletingSurah) return;
    
    try {
      setIsSaving(true);
      await deleteSurah(deletingSurah.id);
      toast.success(t('success'));
      await fetchSurahs();
      setDeletingSurah(null);
    } catch (error) {
      console.error('Failed to delete surah:', error);
      toast.error(t('error'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain-overlay">
      <Header />
      
      <main className="container mx-auto px-6 py-8 pb-32">
        {/* Page Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <div className="text-right">
            <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground">
              {t('surahs')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {filteredSurahs.length} / {surahs.length} {t('surahs')}
            </p>
          </div>
          
          {isAdmin && (
            <Button
              onClick={() => {
                setEditingSurah(null);
                setShowSurahModal(true);
              }}
              className="rounded-full bg-primary hover:bg-primary/90 gap-2"
              data-testid="add-surah-btn"
            >
              <Plus className="h-5 w-5" />
              <span className="hidden sm:inline">{t('addSurah')}</span>
            </Button>
          )}
        </motion.div>

        {onOpenMutashabihat && (
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={onOpenMutashabihat}
            className="mb-6 flex w-full items-center justify-between rounded-2xl border border-amber-500/25 bg-gradient-to-l from-emerald-950/80 to-card p-5 text-right shadow-sm transition hover:border-amber-500/50"
            data-testid="mutashabihat-menu-btn"
          >
            <span className="flex items-center gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-500/10 text-amber-500">
                <BookOpen className="h-6 w-6" />
              </span>
              <span>
                <span className="block font-arabic text-lg font-semibold text-foreground">{t('mutashabihatMenu')}</span>
                <span className="mt-1 block text-sm text-muted-foreground">{t('mutashabihatSubtitle')}</span>
              </span>
            </span>
            <span className="text-xl text-amber-500" aria-hidden="true">←</span>
          </motion.button>
        )}

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <div className="relative max-w-md">
            <Search className="absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground right-3" />
            <Input
              type="text"
              placeholder={t('searchSurahs') || 'Rechercher une sourate...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="rounded-full bg-card border-border pr-10 pl-10"
              data-testid="search-input"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchQuery('')}
                className="absolute top-1/2 -translate-y-1/2 h-6 w-6 rounded-full left-2"
                data-testid="clear-search-btn"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        </motion.div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredSurahs.length === 0 && searchQuery ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center py-20 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
              <Search className="h-10 w-10 text-muted-foreground" />
            </div>
            <h2 className="font-heading text-2xl text-foreground mb-2">
              {t('noResults')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('noMatchFor')} "{searchQuery}"
            </p>
            <Button
              onClick={() => setSearchQuery('')}
              variant="outline"
              className="rounded-full"
            >
              {t('clearSearch')}
            </Button>
          </motion.div>
        ) : surahs.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center py-20 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
              <BookOpen className="h-10 w-10 text-muted-foreground" />
            </div>
            <h2 className="font-heading text-2xl text-foreground mb-2">
              {t('noSurahs')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {isAdmin ? t('addFirstSurah') : t('noSurahAvailable')}
            </p>
            {isAdmin && (
              <Button
                onClick={() => setShowSurahModal(true)}
                className="rounded-full bg-primary hover:bg-primary/90 gap-2"
                data-testid="add-first-surah-btn"
              >
                <Plus className="h-5 w-5" />
                {t('addSurah')}
              </Button>
            )}
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSurahs.map((surah, index) => (
              <SurahCard
                key={surah.id}
                surah={surah}
                index={index}
                onClick={() => onSelectSurah(surah)}
                onEdit={isAdmin ? () => {
                  setEditingSurah(surah);
                  setShowSurahModal(true);
                } : null}
                onDelete={isAdmin ? () => setDeletingSurah(surah) : null}
                showActions={isAdmin}
              />
            ))}
          </div>
        )}
      </main>

      {/* Audio Player */}
      <AudioPlayer />

      {/* Surah Modal */}
      <SurahModal
        isOpen={showSurahModal}
        onClose={() => {
          setShowSurahModal(false);
          setEditingSurah(null);
        }}
        onSave={handleSaveSurah}
        surah={editingSurah}
        isLoading={isSaving}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deletingSurah}
        onClose={() => setDeletingSurah(null)}
        onConfirm={handleDeleteSurah}
        title={t('deleteSurah')}
        description={`${t('confirmDelete')} "${deletingSurah?.name_arabic}"?`}
        isLoading={isSaving}
      />
    </div>
  );
}
